// src/Pages/SingleBlog/SingleBlog.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { fetchPostBySlug } from "../../services/wpApi";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { ArrowLeft } from "lucide-react";
import Spinner from "../../components/Loading/Spinner.jsx";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs.jsx";

// --- Pienet apurit ---
function stripTags(html = "") {
  return String(html).replace(/<[^>]*>/g, "").trim();
}
function smartExcerpt(s = "", max = 160) {
  const clean = stripTags(s).replace(/\s+/g, " ");
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max + 1);
  const i = cut.lastIndexOf(" ");
  return (i > 0 ? cut.slice(0, i) : clean.slice(0, max)).trim();
}

// Nosta feature-kuva tai palauta null
function getFeatured(post) {
  const media =
    post?._embedded?.["wp:featuredmedia"] &&
    post._embedded["wp:featuredmedia"][0];
  if (!media?.source_url) return null;
  return {
    url: media.source_url,
    alt: media?.alt_text || stripTags(media?.title?.rendered || ""),
  };
}

// Siisti WP HTML
function cleanWpHtml(html = "") {
  return String(html)
    .replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "")
    .replace(/(?:<br\s*\/?>\s*){2,}/gi, "<br />");
}

export default function SingleBlog() {
  const { slug } = useParams();
  const rrLocation = useLocation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setPost(null);

    fetchPostBySlug(slug)
      .then((p) => !cancelled && setPost(p))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Otsikko muruihin heti
  const crumbTitle = useMemo(
    () =>
      post?.title?.rendered
        ? stripTags(post.title.rendered)
        : "Artikkeli",
    [post]
  );

  // Meta-arvot (turvalliset myös lataus/virhetilassa)
  const featured = getFeatured(post);
  const rawTitle = post?.title?.rendered || "Artikkeli";
  const title = `${stripTags(rawTitle)} | Buukkeri`;
  const description =
    smartExcerpt(
      post?.excerpt?.rendered || post?.content?.rendered || ""
    );
  const canonical =
    typeof window !== "undefined"
      ? `${window.location.origin}/blogi/${encodeURIComponent(slug)}`
      : `/blogi/${encodeURIComponent(slug)}`;
  const published = post?.date ? new Date(post.date).toISOString() : null;
  const modified = post?.modified
    ? new Date(post.modified).toISOString()
    : published;

  // Lähetä vapaaehtoinen otsikkotapahtuma (mittauksia varten)
  useEffect(() => {
    if (!title) return;
    const path = `${rrLocation.pathname}${rrLocation.search || ""}`;
    window.dispatchEvent(
      new CustomEvent("bbx:title", { detail: { title, path } })
    );
  }, [title, rrLocation.pathname, rrLocation.search]);

  // Sisältö HTML (puhdistettuna)
  const contentHtml = cleanWpHtml(post?.content?.rendered || "");

  /* ---------- Lataus / virhetilat ---------- */

  if (loading) {
    return (
      <section className="max-w-6xl mx-auto px-4 pt-24 pb-24">
        <Helmet>
          <title>{`Ladataan… | Blogi | Buukkeri`}</title>
          <link rel="canonical" href={canonical} />
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>

        <div className="mb-3">
          <Breadcrumbs
            items={[
              { label: "Etusivu", to: "/" },
              { label: "Blogi", to: "/blogi" },
              { label: "Ladataan…" },
            ]}
          />
        </div>
        <div className="py-16 flex justify-center">
          <Spinner size="lg" showLabel label="Ladataan artikkelia…" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-16">
        <Helmet>
          <title>{`Virhe – Artikkeli | Buukkeri`}</title>
          <link rel="canonical" href={canonical} />
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>

        <div className="mb-3">
          <Breadcrumbs
            items={[
              { label: "Etusivu", to: "/" },
              { label: "Blogi", to: "/blogi" },
              { label: "Artikkeli" },
            ]}
          />
        </div>

        <p className="text-red-600">Virhe: {error}</p>
        <div className="mt-4">
          <NeonButton
            onClick={() => navigate("/blogi")}
            variant="primary"
            size="sm"
            IconLeft={ArrowLeft}
            aria-label="Takaisin blogiin"
          >
            Takaisin blogiin
          </NeonButton>
        </div>
      </section>
    );
  }

  if (!post) return null;

  const author =
    post?._embedded?.author?.[0]?.name || "Tuntematon kirjoittaja";
  const dateHuman = new Date(post.date).toLocaleDateString("fi-FI");

  /* ---------- Varsinainen näkymä ---------- */

  return (
    <div className="relative pt-24 pb-16">
      {/* Taustagradientti koko leveydelle */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 bg-gradient-to-b from-bile-dark via-bile-dark to-black"
      />

      {/* META */}
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />

        {/* OG / Twitter */}
        <meta property="og:locale" content="fi_FI" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        {featured?.url && (
          <meta property="og:image" content={featured.url} />
        )}
        {published && (
          <meta
            property="article:published_time"
            content={published}
          />
        )}
        {modified && (
          <meta
            property="article:modified_time"
            content={modified}
          />
        )}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        {featured?.url && (
          <meta name="twitter:image" content={featured.url} />
        )}
      </Helmet>

      {/* Sisältöcontainer */}
      <div className="relative max-w-5xl md:max-w-6xl mx-auto px-4 md:px-6">
        {/* Murupolku */}
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { label: "Etusivu", to: "/" },
              { label: "Blogi", to: "/blogi" },
              { label: crumbTitle },
            ]}
          />
        </div>

        {/* Kortti */}
        <div className="rounded-2xl md:rounded-3xl p-6 md:p-10 bg-lemon/5 dark:bg-black/80 backdrop-blur-md shadow-2xl ring-1 ring-white/10">
          <div className="mb-6">
            <NeonButton
              onClick={() => navigate("/blogi")}
              variant="primary"
              className="scale-[0.85] md:scale-100 font-semibold"
              size="sm"
              IconLeft={ArrowLeft}
              aria-label="Takaisin blogiin"
            >
              Takaisin blogiin
            </NeonButton>
          </div>

          <article className="prose-img:rounded-2xl">
            <h1
              className="!mb-2 text-3xl md:text-4xl font-bold text-black leading-tight dark:text-white"
              dangerouslySetInnerHTML={{ __html: post.title.rendered }}
            />
            <p className="dark:text-white/80 text-black/80 font-customFont1 text-xs md:text-sm mt-0">
              {author} • {dateHuman}
            </p>

            {featured && (
              <figure className="my-6 overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={featured.url}
                  alt={featured.alt || ""}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            )}

            {/* Varsinainen artikkelisän sisältö – leveämpi prose */}
            <div
              className="
                prose max-w-none dark:prose-invert
                leading-7 md:leading-8
                prose-sm sm:prose-base md:prose-lg
                prose-h1:text-3xl md:prose-h1:text-4xl prose-h1:leading-tight
                prose-h2:text-2xl md:prose-h2:text-3xl
                prose-h3:text-xl  md:prose-h3:text-2xl
                prose-p:text-[16px] sm:prose-p:text-[17px] md:prose-p:text-[18px]
                prose-li:text-[16px] sm:prose-li:text-[17px] md:prose-li:text-[18px]
                prose-a:text-pink-700 dark:prose-a:text-pink-300 hover:prose-a:opacity-90
                prose-img:rounded-2xl
                prose-headings:mt-[0.9em] prose-headings:mb-[0.35em]
                prose-h1:mt-[1.05em] prose-h1:mb-[0.35em]
                prose-h2:mt-[0.95em] prose-h2:mb-[0.3em]
                prose-h3:mt-[0.8em]  prose-h3:mb-[0.25em]
                prose-p:my-[0.55em]
                prose-li:my-[0.15em]
                prose-ul:my-[0.5em] prose-ol:my-[0.5em]
                prose-img:my-[0.75em]
              "
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />

            {/* Kategoriat */}
            {post?._embedded?.["wp:term"]?.[0]?.length ? (
              <div className="mt-8 flex flex-wrap gap-2">
                {post._embedded["wp:term"][0].map((cat) => (
                  <span
                    key={cat.id}
                    className="px-2 py-1 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 dark:text-white/80 text-black/80"
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            ) : null}
          </article>
        </div>
      </div>
    </div>
  );
}
